/*
 * BioIndex Lifeform choices. Firestore continues to store species.forms[].label
 * as a STRING. Existing records are not rewritten by this feature.
 */
import {
  generaByFamily,
  familiesByOrder,
  ordersByClass,
  classesByPhylum,
  phylaByKingdom,
} from "./taxonomy";

const alphabetize = (values) => [...values].sort((a, b) =>
  a.localeCompare(b, "en", { sensitivity: "base" }),
);

export const LIFEFORM_KINGDOMS = alphabetize(["Animalia", "Plantae", "Fungi", "Chromista", "Protista"]);

// Animal life stages are hierarchical. The parent (Adult / Juvenile) is a
// complete choice only when no more specific form has been selected.
export const ADULT_LIFEFORM_OPTIONS = alphabetize([
  "Adult Breeding Plumage",
  "Adult Non-Breeding Plumage",
  "Butterfly",
  "Female Adult",
  "Male Adult",
  "Moth",
  "Queen",
]);

export const JUVENILE_LIFEFORM_OPTIONS = alphabetize([
  "Chick",
  "Female Juvenile",
  "First Winter Plumage",
  "Male Juvenile",
]);

export const ANIMAL_LIFEFORM_CHILDREN_BY_PARENT = {
  Adult: ADULT_LIFEFORM_OPTIONS,
  Juvenile: JUVENILE_LIFEFORM_OPTIONS,
};

// These two obsolete labels can still occur on previously saved photos.
// Preserve their parent/child semantics without offering them for new picks.
const LEGACY_ADULT_DETAILS = ["Adult Breeding", "Adult Non-Breeding"];

// Plant structures follow the same nonredundant parent/detail pattern:
// selecting a detail replaces Flower, Fruit, or Leaf in the saved label.
export const FLOWER_LIFEFORM_OPTIONS = alphabetize([
  "Immature Flower", "Wilted Flower",
]);

export const FRUIT_LIFEFORM_OPTIONS = alphabetize([
  "Immature Fruit", "Ripe Fruit", "Rotten Fruit",
]);

export const LEAF_LIFEFORM_OPTIONS = alphabetize([
  "Immature Leaf", "Senescent Leaf",
]);

export const PLANT_LIFEFORM_CHILDREN_BY_PARENT = {
  Flower: FLOWER_LIFEFORM_OPTIONS,
  Fruit: FRUIT_LIFEFORM_OPTIONS,
  Leaf: LEAF_LIFEFORM_OPTIONS,
};

export const LIFEFORM_CHILDREN_BY_PARENT = {
  ...ANIMAL_LIFEFORM_CHILDREN_BY_PARENT,
  ...PLANT_LIFEFORM_CHILDREN_BY_PARENT,
};

export function lifeformDetailsFor(parent) {
  return LIFEFORM_CHILDREN_BY_PARENT[parent] || [];
}

function lifeformDetailsIncludingLegacy(parent) {
  const current = lifeformDetailsFor(parent);
  return parent === "Adult" ? [...current, ...LEGACY_ADULT_DETAILS] : current;
}

export const LIFEFORM_OPTIONS_BY_KINGDOM = {
  Animalia: alphabetize([
    "Adult", "Juvenile", "Immature", "Egg", "Nest",
    "Larva", "Larvae", "Caterpillar", "Pupa", "Nymph", "Tadpole",
    "Other",
  ]),
  Plantae: alphabetize([
    "Whole plant", "Seedling", "Leaf", "Flower", "Fruit", "Seed", "Bud",
    "Stem", "Trunk", "Root", "Cone", "Other",
  ]),
  Fungi: alphabetize([
    "Mushroom", "Fruiting body", "Cap", "Gills", "Pores", "Stipe",
    "Mycelium", "Spores", "Other",
  ]),
  Chromista: alphabetize([
    "Whole organism", "Colony", "Frond", "Blade", "Stipe", "Holdfast",
    "Reproductive structure", "Other",
  ]),
  Protista: alphabetize(["Whole organism", "Colony", "Cyst", "Other"]),
};

// Deliberately concise: displayed on hover/focus within the Lifeform popup.
const DESCRIPTIONS = {
  Adult: "Fully developed organism.",
  "Adult Breeding": "Sexually mature animal in a breeding state.",
  "Adult Breeding Plumage": "Adult feathers worn during breeding.",
  "Adult Non-Breeding": "Sexually mature animal outside the breeding state.",
  "Adult Non-Breeding Plumage": "Adult feathers outside the breeding season.",
  Bark: "Protective outer covering of woody stems.",
  Blade: "Broad, flattened part of an alga.",
  Breeding: "Showing signs of reproductive activity.",
  Bud: "Undeveloped shoot, leaf, or flower.",
  Butterfly: "Adult day-flying butterfly.",
  Cap: "Upper part of a mushroom.",
  Caterpillar: "Larval stage of a butterfly or moth.",
  Chick: "Young bird, typically recently hatched.",
  Colony: "Organisms growing or living together.",
  Cone: "Seed- or pollen-bearing plant structure.",
  Cyst: "Protected, often dormant life stage.",
  Egg: "Reproductive structure containing an embryo.",
  Female: "Female individual or reproductive form.",
  "Female Adult": "Fully developed female animal.",
  "Female Juvenile": "Young female animal not yet mature.",
  "First Winter Plumage": "Feathers worn during a bird's first winter.",
  Flower: "Reproductive structure of a flowering plant.",
  Frond: "Leaf-like or branching body structure.",
  Fruit: "Mature seed-bearing plant structure.",
  "Fruiting body": "Spore-producing structure of a fungus.",
  Gills: "Spore-bearing plates beneath a mushroom cap.",
  Holdfast: "Structure anchoring an alga to a surface.",
  Immature: "Not yet fully developed.",
  "Immature Flower": "Flower still developing or not yet open.",
  "Immature Fruit": "Fruit not yet fully developed.",
  "Immature Leaf": "Leaf still developing or expanding.",
  Juvenile: "Young animal not yet mature.",
  Larva: "Single immature stage before metamorphosis.",
  Larvae: "Multiple immature stages before metamorphosis.",
  Leaf: "Plant organ mainly used for photosynthesis.",
  Leaves: "Plant organs mainly used for photosynthesis.",
  Male: "Male individual or reproductive form.",
  "Male Adult": "Fully developed male animal.",
  "Male Juvenile": "Young male animal not yet mature.",
  Moth: "Adult moth.",
  Mushroom: "Visible fruiting body of a fungus.",
  Mycelium: "Network of fungal filaments.",
  Nest: "Structure used to shelter eggs or young.",
  "Non-Breeding": "Not displaying reproductive activity.",
  Nymph: "Young stage of incomplete metamorphosis.",
  Other: "Another form not listed here.",
  Pores: "Small openings releasing fungal spores.",
  Pupa: "Stage between larva and adult.",
  Queen: "Reproductive female in a social colony.",
  "Ripe Fruit": "Fruit that has reached its ripening stage.",
  "Rotten Fruit": "Decaying fruit, typically soft or decomposing.",
  "Reproductive structure": "Part involved in producing offspring.",
  Root: "Plant organ anchoring or absorbing resources.",
  Seed: "Plant embryo with stored reserves.",
  Seedling: "Young plant growing from a seed.",
  "Senescent Leaf": "Aging leaf, often yellowing or dying.",
  Spores: "Microscopic fungal reproductive units.",
  Stem: "Plant axis supporting leaves or branches.",
  Stipe: "Stalk supporting a cap or frond.",
  Tadpole: "Aquatic larval stage of an amphibian.",
  Trunk: "Main woody stem of a tree.",
  "Wilted Flower": "Flower losing firmness or drooping.",
  "Whole organism": "The organism shown substantially in full.",
  "Whole plant": "The plant shown substantially in full.",
};

export function lifeformDescriptionFor(value) {
  return DESCRIPTIONS[value] || "Previously saved Lifeform label.";
}

// Retained for other code importing the historical constant.
export const FORM_OPTIONS = alphabetize([
  ...new Set([
    ...Object.values(LIFEFORM_OPTIONS_BY_KINGDOM).flat(),
    ...ADULT_LIFEFORM_OPTIONS,
    ...JUVENILE_LIFEFORM_OPTIONS,
    ...FLOWER_LIFEFORM_OPTIONS,
    ...FRUIT_LIFEFORM_OPTIONS,
    ...LEAF_LIFEFORM_OPTIONS,
    // Old values remain recognizable without offering them as new root choices.
    "Male", "Female", "Breeding", "Non-Breeding", "Leaves",
  ]),
]);

const norm = (name) => String(name || "").trim().toLowerCase();

function parentsByChild(groups) {
  const parents = new Map();
  Object.entries(groups || {}).forEach(([parent, children]) => {
    (children || []).forEach((child) => {
      const key = norm(child);
      if (!key) return;
      if (!parents.has(key)) parents.set(key, new Set());
      parents.get(key).add(norm(parent));
    });
  });
  return parents;
}

const familyParents = parentsByChild(generaByFamily);
const orderParents = parentsByChild(familiesByOrder);
const classParents = parentsByChild(ordersByClass);
const phylumParents = parentsByChild(classesByPhylum);
const kingdomParents = parentsByChild(phylaByKingdom);

// Only infer a kingdom when every complete taxonomic path agrees.
export function resolveLifeformKingdom(scientificName) {
  const genus = norm(String(scientificName || "").split(/\s+/)[0]);
  if (!genus) return "";
  const seen = new Set();
  for (const family of familyParents.get(genus) || []) {
    for (const order of orderParents.get(family) || []) {
      for (const className of classParents.get(order) || []) {
        for (const phylum of phylumParents.get(className) || []) {
          for (const kingdom of kingdomParents.get(phylum) || []) {
            const accepted = LIFEFORM_KINGDOMS.find((k) => norm(k) === kingdom);
            if (accepted) seen.add(accepted);
          }
        }
      }
    }
  }
  return seen.size === 1 ? [...seen][0] : "";
}

export function lifeformOptionsFor(kingdom) {
  return LIFEFORM_OPTIONS_BY_KINGDOM[kingdom] || [];
}

const LEGACY_COMBINATIONS = {
  "adult & juvenile": ["Adult", "Juvenile"],
  "male & female": ["Male", "Female"],
  "male, female, & juvenile": ["Male", "Female", "Juvenile"],
  "immature male": ["Immature", "Male"],
  "immature female": ["Immature", "Female"],
};

export function parseLifeformSelection(label) {
  if (Array.isArray(label)) {
    return [...new Set(label.map((v) => String(v || "").trim()).filter(Boolean))];
  }
  const value = String(label || "").trim();
  if (!value || value.toLowerCase() === "default") return [];
  const legacy = LEGACY_COMBINATIONS[value.toLowerCase()];
  if (legacy) return [...legacy];
  // The canonical storage format is a comma-separated string, never an array.
  return [...new Set(value.split(/\s*,\s*/).map((part) => part.trim()).filter(Boolean))];
}

export function serializeLifeformSelection(values) {
  return normalizeLifeformSelection(values).join(", ");
}

// Parent and descendant must never be saved redundantly together.
// This also handles historical values such as "Adult, Male Adult" from the
// previous picker, and "Leaves, Immature Leaf" with an obsolete plural label.
// Unrelated branches can still be selected independently.
export function normalizeLifeformSelection(values) {
  const selected = [...new Set((Array.isArray(values) ? values : [])
    .map((value) => String(value || "").trim())
    .filter(Boolean))];
  return selected.filter((value) => {
    // The old "Leaves" label remains unchanged until the user selects a newer
    // Leaf choice. Never write "Leaves, Leaf" or "Leaves, Immature Leaf".
    if (value === "Leaves" && (
      selected.includes("Leaf") ||
      LEAF_LIFEFORM_OPTIONS.some((child) => selected.includes(child))
    )) return false;
    const descendants = lifeformDetailsIncludingLegacy(value);
    return descendants.length === 0 || !descendants.some((child) => selected.includes(child));
  });
}

export function toggleLifeformSelection(previous, item) {
  const current = normalizeLifeformSelection(previous);
  const descendants = lifeformDetailsIncludingLegacy(item);

  if (descendants.length > 0) {
    if (current.includes(item)) {
      // A second click on the selected broad life stage deselects that stage.
      return current.filter((value) => value !== item && !descendants.includes(value));
    }
    // Clicking the parent of selected refinements returns to the broad stage.
    return normalizeLifeformSelection([
      ...current.filter((value) => !descendants.includes(value)), item,
    ]);
  }

  const parent = Object.keys(LIFEFORM_CHILDREN_BY_PARENT).find((key) =>
    lifeformDetailsIncludingLegacy(key).includes(item),
  );
  if (parent) {
    if (current.includes(item)) {
      const without = current.filter((value) => value !== item);
      // If the last refinement was unchecked, leave the broad stage selected.
      return without.some((value) => lifeformDetailsIncludingLegacy(parent).includes(value))
        ? without : [...without, parent];
    }
    // A specific choice REPLACES its parent; it is never added alongside it.
    return normalizeLifeformSelection([
      ...current.filter((value) => value !== parent), item,
    ]);
  }

  return current.includes(item)
    ? current.filter((value) => value !== item)
    : [...current, item];
}
